/**
 * Domains — the ones this organization proved, how to prove another, and what
 * an address at one does when it signs in.
 *
 * Proving is a DNS record the page names in full — its name and its value, each
 * copyable — and *Check* asks DNS for it; a domain that is not there yet stays
 * on the list as "not yet", with when it was last looked for. Who an address at
 * a verified domain lets in is one choice, and the requests it produces when
 * the choice is *approval* are answered here, beside the domains that made them.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Field, Input, PrimaryButton, Row, RowAction, StatusCell, Table } from '../../components/primitives';
import { SettingSelect } from '../../components/settings-row';
import { useConfirm } from '../../components/Modal';
import { api, apiPath, prefix } from '../../lib/api';
import { isoDay } from '../../lib/format';
import type { DomainJoin, OrgDomain, OrgIdentity } from '../../lib/types';
import type { SectionProps } from './Security';

const JOINS: { value: DomainJoin; label: string }[] = [
  { value: 'approval', label: 'Ask an administrator first' },
  { value: 'auto', label: 'Join as a member at once' },
  { value: 'off', label: 'Do nothing' }
];

export function DomainsSection({ organization, identity, apply }: SectionProps): React.JSX.Element {
  const [domain, setDomain] = useState('');
  const [dialog, ask] = useConfirm();
  const base = apiPath`/orgs/${organization.id}/identity`;

  const add = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const added = await apply(async () => {
      await api.post<{ domain: OrgDomain }>(apiPath`${prefix(base)}/domains`, { domain });
      return await api.get<OrgIdentity>(base);
    });
    if (added) setDomain('');
  };

  const check = (row: OrgDomain): void =>
    void apply(async () => {
      await api.post<{ domain: OrgDomain }>(apiPath`${prefix(base)}/domains/${row.id}/check`);
      return await api.get<OrgIdentity>(base);
    });

  const remove = async (row: OrgDomain): Promise<void> => {
    const agreed = await ask({
      title: `Remove ${row.domain}?`,
      lede: row.verifiedAt
        ? `Addresses at ${row.domain} stop joining ${organization.name}, and single sign-on stops being required for them. Nobody already here is removed.`
        : `It was never proved, so nothing changes for anybody.`,
      confirm: 'Remove it',
      danger: true
    });
    if (agreed) void apply(() => api.del<OrgIdentity>(apiPath`${prefix(base)}/domains/${row.id}`));
  };

  const columns = '1.1fr 2fr 100px 110px';

  return (
    <>
      {dialog}
      <Table
        columns={columns}
        header={['Domain', 'DNS record', 'State', '']}
        toolbar={
          <form onSubmit={(event) => void add(event)} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Add a domain" htmlFor="sec-domain">
              <Input id="sec-domain" value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="acme.com" />
            </Field>
            <PrimaryButton type="submit" tone="ink" size="sm">
              Add
            </PrimaryButton>
          </form>
        }
      >
        {identity.domains.length === 0 ? (
          <Row columns={columns}>
            <span style={{ color: color.inkFaint }}>No domain yet. Add the one your people’s addresses are at.</span>
          </Row>
        ) : (
          identity.domains.map((row) => (
            <Row key={row.id} columns={columns}>
              <span style={{ fontWeight: 500 }}>{row.domain}</span>
              <div style={{ minWidth: 0, fontFamily: font.mono, fontSize: 11.5, lineHeight: 1.6 }}>
                {row.verifiedAt ? (
                  <span style={{ color: color.inkFaint }}>Proved {isoDay(row.verifiedAt)}. The record can be deleted.</span>
                ) : (
                  <>
                    <div>
                      TXT <span style={{ userSelect: 'all' }}>{row.record.name}</span>
                    </div>
                    <div style={{ userSelect: 'all', wordBreak: 'break-all' }}>{row.record.value}</div>
                    {row.checkedAt && <div style={{ color: color.inkFaint }}>Not found at {isoDay(row.checkedAt)}.</div>}
                  </>
                )}
              </div>
              {row.verifiedAt ? <StatusCell tone="ok" word="verified" /> : <StatusCell tone="warn" word="not yet" ring />}
              <div style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                {!row.verifiedAt && <RowAction onClick={() => check(row)}>Check</RowAction>}
                <RowAction danger onClick={() => void remove(row)}>
                  Remove
                </RowAction>
              </div>
            </Row>
          ))
        )}
      </Table>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14 }}>When somebody at a verified domain signs in</span>
        <SettingSelect
          value={identity.policy.domainJoin}
          options={JOINS}
          wide
          onChange={(value) => void apply(() => api.patch<OrgIdentity>(apiPath`${prefix(base)}/policy`, { domainJoin: value }))}
        />
      </div>
      <p style={{ margin: 0, fontSize: 13, color: color.inkSubtle, lineHeight: 1.6, maxWidth: 640 }}>
        Only an address somebody has proved counts: a confirmed email, GitHub’s verified one, or your identity provider’s.
        Joining gives no seat — that is still given on Members.
      </p>

      {identity.requests.length > 0 && (
        <Table columns="1.4fr 1fr 110px 150px" header={['Asking to join', 'Name', 'Since', '']}>
          {identity.requests.map((request) => (
            <Row key={request.userId} columns="1.4fr 1fr 110px 150px">
              <span>{request.email}</span>
              <span style={{ color: color.inkSubtle }}>{request.name ?? '—'}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{isoDay(request.requestedAt)}</span>
              <div style={{ display: 'flex', gap: 12 }}>
                <RowAction
                  onClick={() => void apply(() => api.post<OrgIdentity>(apiPath`${prefix(base)}/requests/${request.userId}`, { decision: 'approve' }))}
                >
                  Let in
                </RowAction>
                <RowAction
                  danger
                  onClick={() => void apply(() => api.post<OrgIdentity>(apiPath`${prefix(base)}/requests/${request.userId}`, { decision: 'decline' }))}
                >
                  Decline
                </RowAction>
              </div>
            </Row>
          ))}
        </Table>
      )}
    </>
  );
}
